/**
 * Spirit Island has no VP track — “scoring” is Terror-gated victory / defeat.
 * Numeric Fear threshold per player count is not in the local PEŁNA źródło
 * (only “zależny od liczby graczy” + illustrative example) — keep it
 * component-dependent. Do not invent adversary / scenario defeat texts.
 */

export type SpiritIslandScoringModuleId =
  | 'base'
  | 'branch-and-claw'
  | 'jagged-earth'
  | 'feather-and-flame';

export type SpiritIslandScoringTiming = 'ongoing' | 'end-game' | 'both' | 'mid-game';

export type SpiritIslandScoringSourceStatus =
  | 'verified'
  | 'component-dependent'
  | 'partial'
  | 'blocked';

export type SpiritIslandScoringRecord = {
  id: string;
  gameId: 'spirit-island';
  moduleId: SpiritIslandScoringModuleId;
  category: 'victory' | 'defeat' | 'fear' | 'timing' | 'expansion';
  element: string;
  timing: SpiritIslandScoringTiming;
  scoring: string;
  conditions: string;
  exceptions: string;
  returnsMeeple: string;
  sourceStatus: SpiritIslandScoringSourceStatus;
  verified: boolean;
  order: number;
  keywords: string[];
};

export const spiritIslandScoringRecords: SpiritIslandScoringRecord[] = [
  {
    id: 'si-terror-i',
    gameId: 'spirit-island',
    moduleId: 'base',
    category: 'victory',
    element: 'Terror I — zwycięstwo',
    timing: 'both',
    scoring: 'Na wyspie nie może być żadnego Najeźdźcy (Odkrywcy, Osady, Miasta).',
    conditions:
      'Sprawdzaj dopiero po pełnym rozpatrzeniu bieżącej akcji lub efektu, chyba że karta mówi inaczej.',
    exceptions: 'Nie ogłaszaj wygranej w środku nierozpatrzonej karty Strachu / Mocy.',
    returnsMeeple: 'N/A — gra kooperacyjna, bez toru VP.',
    sourceStatus: 'verified',
    verified: true,
    order: 10,
    keywords: ['terror', 'I', 'zwycięstwo', 'najeźdźcy'],
  },
  {
    id: 'si-terror-ii',
    gameId: 'spirit-island',
    moduleId: 'base',
    category: 'victory',
    element: 'Terror II — zwycięstwo',
    timing: 'both',
    scoring: 'Na wyspie nie może być Osad ani Miast (Odkrywcy mogą zostać).',
    conditions: 'Obowiązuje od odsłonięcia karty Terroru II.',
    exceptions: 'Przy Terrorze I Odkrywcy nadal blokują zwycięstwo.',
    returnsMeeple: 'N/A',
    sourceStatus: 'verified',
    verified: true,
    order: 20,
    keywords: ['terror', 'II', 'osady', 'miasta'],
  },
  {
    id: 'si-terror-iii',
    gameId: 'spirit-island',
    moduleId: 'base',
    category: 'victory',
    element: 'Terror III — zwycięstwo',
    timing: 'both',
    scoring: 'Na wyspie nie może być Miast.',
    conditions: 'Obowiązuje od odsłonięcia karty Terroru III.',
    exceptions: '—',
    returnsMeeple: 'N/A',
    sourceStatus: 'verified',
    verified: true,
    order: 30,
    keywords: ['terror', 'III', 'miasta'],
  },
  {
    id: 'si-terror-iv',
    gameId: 'spirit-island',
    moduleId: 'base',
    category: 'victory',
    element: 'Terror IV — natychmiastowe zwycięstwo',
    timing: 'both',
    scoring: 'Natychmiastowe zwycięstwo — nie wymaga czyszczenia planszy z Najeźdźców.',
    conditions: 'Odsłonięcie karty Terroru IV.',
    exceptions: 'Nie myl z Terrorem III (brak Miast).',
    returnsMeeple: 'N/A',
    sourceStatus: 'verified',
    verified: true,
    order: 40,
    keywords: ['terror', 'IV', 'natychmiast'],
  },
  {
    id: 'si-defeat-blight',
    gameId: 'spirit-island',
    moduleId: 'base',
    category: 'defeat',
    element: 'Porażka — brak Zarazy w puli',
    timing: 'both',
    scoring: 'Natychmiastowa porażka, gdy trzeba dodać Zarazę, a w puli nie ma znacznika.',
    conditions: 'Natychmiast po sytuacji wymagającej dodania Zarazy bez znacznika.',
    exceptions: 'Nie „dogrywaj rundy” po tej sytuacji.',
    returnsMeeple: 'N/A',
    sourceStatus: 'verified',
    verified: true,
    order: 50,
    keywords: ['porażka', 'zaraza', 'pula'],
  },
  {
    id: 'si-defeat-presence',
    gameId: 'spirit-island',
    moduleId: 'base',
    category: 'defeat',
    element: 'Porażka — utrata całej Obecności',
    timing: 'both',
    scoring:
      'Natychmiastowa porażka całej drużyny, gdy dowolny Duch traci całą Obecność z wyspy.',
    conditions: 'Po pełnym rozpatrzeniu efektu, który usunął ostatnią Obecność tego Ducha.',
    exceptions: 'Obecność partnera nie chroni przed tą porażką.',
    returnsMeeple: 'N/A',
    sourceStatus: 'verified',
    verified: true,
    order: 60,
    keywords: ['porażka', 'obecność'],
  },
  {
    id: 'si-defeat-invader-deck',
    gameId: 'spirit-island',
    moduleId: 'base',
    category: 'defeat',
    element: 'Porażka — brak kart Najeźdźców',
    timing: 'both',
    scoring:
      'Natychmiastowa porażka, gdy zabraknie kart Najeźdźców w chwili, gdy należy dobrać kolejną.',
    conditions: 'Przy dobieraniu karty Najeźdźców.',
    exceptions: '—',
    returnsMeeple: 'N/A',
    sourceStatus: 'verified',
    verified: true,
    order: 70,
    keywords: ['porażka', 'najeźdźcy', 'talia'],
  },
  {
    id: 'si-fear-buildings',
    gameId: 'spirit-island',
    moduleId: 'base',
    category: 'fear',
    element: 'Strach za zniszczenie budynków',
    timing: 'mid-game',
    scoring:
      'Zniszczenie Osady → 1 Strach; zniszczenie Miasta → 2 Strachu. Samo zadanie obrażeń bez zniszczenia nie generuje Strachu. Źródło nie przypisuje Strachu za zniszczenie Odkrywcy.',
    conditions: 'Tylko przy zniszczeniu (nie przy samym „usuń”, jeśli czasownik na karcie nie jest zniszczeniem).',
    exceptions:
      'Pełny katalog „zniszcz / usuń / zastąp” na wszystkich kartach — DO WERYFIKACJI; trzymaj się czasownika na komponencie.',
    returnsMeeple: 'N/A',
    sourceStatus: 'verified',
    verified: true,
    order: 80,
    keywords: ['strach', 'osada', 'miasto', '1', '2'],
  },
  {
    id: 'si-fear-threshold',
    gameId: 'spirit-island',
    moduleId: 'base',
    category: 'fear',
    element: 'Próg Strachu (liczba graczy)',
    timing: 'mid-game',
    scoring:
      'Gdy wygenerujesz liczbę Strachu równą progowi zależnemu od liczby graczy: zdobądź wierzchnią kartę Strachu (do późniejszego rozpatrzenia), zwróć znaczniki do puli, nadmiar przechodzi na kolejną kartę. Konkretna tabela progów — odczyt z pudełka / pomocy; lokalne źródło nie podaje pełnej tabeli (przykład ilustracyjny używa progu 4).',
    conditions: 'Przy generowaniu Strachu w trakcie gry.',
    exceptions: 'Nie inventuj tabeli 1–4 graczy z pamięci w aplikacji.',
    returnsMeeple: 'N/A',
    sourceStatus: 'component-dependent',
    verified: true,
    order: 90,
    keywords: ['strach', 'próg', 'graczy'],
  },
  {
    id: 'si-check-timing',
    gameId: 'spirit-island',
    moduleId: 'base',
    category: 'timing',
    element: 'Kiedy sprawdzać zwycięstwo / porażkę',
    timing: 'both',
    scoring:
      'Najpierw dokończ bieżącą akcję lub efekt w całości, potem sprawdź warunki. Karty Strachu rozpatruj w Fazie Najeźdźców (po Wydarzeniu, przed Odwetem), nie w chwili zdobycia.',
    conditions: 'Po każdym w pełni rozpatrzonym efekcie, który mógł zmienić stan wyspy / Terroru.',
    exceptions: 'Karta może mówić inaczej — tekst komponentu ma pierwszeństwo.',
    returnsMeeple: 'N/A',
    sourceStatus: 'verified',
    verified: true,
    order: 100,
    keywords: ['timing', 'sprawdź', 'strach'],
  },
  {
    id: 'si-same-effect-edge',
    gameId: 'spirit-island',
    moduleId: 'base',
    category: 'timing',
    element: 'Zwycięstwo i porażka z tego samego efektu',
    timing: 'both',
    scoring:
      'Jeśli zwycięstwo i porażka wynikają z tego samego w pełni rozpatrzonego efektu, zastosuj oficjalne reguły kolejności FAQ dla tej edycji. Lokalne źródło: [DO WERYFIKACJI] — nie zgaduj przy stole.',
    conditions: 'Jeden efekt jednocześnie spełnia warunek wygranej i przegranej.',
    exceptions: 'Nie inventuj kolejności „najpierw wygrana” bez FAQ.',
    returnsMeeple: 'N/A',
    sourceStatus: 'blocked',
    verified: false,
    order: 110,
    keywords: ['faq', 'edge', 'remis', 'kolejność'],
  },
  {
    id: 'si-blighted-island-defeat',
    gameId: 'spirit-island',
    moduleId: 'base',
    category: 'defeat',
    element: 'Wyspa Spustoszona — specjalna porażka',
    timing: 'mid-game',
    scoring:
      'Karta Wyspy Spustoszonej może wprowadzać własny warunek porażki lub trwały efekt — odczytaj tekst z komponentu; nie zgaduj wartości puli ani efektu.',
    conditions:
      'Po wyczerpaniu puli Wyspy Zdrowej i odwróceniu karty; w Fazie Najeźdźców najpierw sprawdź Wyspę Spustoszoną.',
    exceptions: 'Tekst konkretnej karty Wyspy jest component-dependent.',
    returnsMeeple: 'N/A',
    sourceStatus: 'component-dependent',
    verified: true,
    order: 120,
    keywords: ['spustoszona', 'wyspa', 'porażka'],
  },
  {
    id: 'si-bc-adversary-scenario',
    gameId: 'spirit-island',
    moduleId: 'branch-and-claw',
    category: 'expansion',
    element: 'Zarośla i Szpony — Przeciwnik / Wydarzenia a koniec gry',
    timing: 'both',
    scoring:
      'Aktywny Przeciwnik lub Scenariusz może dodać własny warunek porażki / zmodyfikować Terror. Teksty Przeciwników, Scenariuszy i Wydarzeń — z komponentów; brak osobnego toru VP.',
    conditions: 'Moduł Zarośla i Szpony (lub Przeciwnik/Scenariusz) włączony.',
    exceptions: 'Katalog Przeciwników / Scenariuszy / Wydarzeń: DO WERYFIKACJI.',
    returnsMeeple: 'N/A',
    sourceStatus: 'component-dependent',
    verified: true,
    order: 200,
    keywords: ['zarośla', 'przeciwnik', 'wydarzenie'],
  },
  {
    id: 'si-je-scenarios',
    gameId: 'spirit-island',
    moduleId: 'jagged-earth',
    category: 'expansion',
    element: 'Rozdarta Ziemia — Scenariusze / Izolacja',
    timing: 'both',
    scoring:
      'Scenariusze dodatku mogą zmieniać warunki końca. Izolacja zmienia sąsiedztwo / celowanie według reguł dodatku — nie tworzy osobnego toru VP. Teksty scenariuszy: z komponentów.',
    conditions: 'Moduł Rozdarta Ziemia włączony.',
    exceptions: 'Pełny katalog scenariuszy: DO WERYFIKACJI.',
    returnsMeeple: 'N/A',
    sourceStatus: 'component-dependent',
    verified: true,
    order: 210,
    keywords: ['rozdarta', 'scenariusz', 'izolacja'],
  },
  {
    id: 'si-ff-no-vp',
    gameId: 'spirit-island',
    moduleId: 'feather-and-flame',
    category: 'expansion',
    element: 'Pióra i Płomienie — brak osobnego scoringu',
    timing: 'both',
    scoring:
      'Dodatek dodaje Duchów / zawartość promocyjną; warunki Terroru i porażki bazy pozostają. Teksty Duchów — z plansz; nie inventuj punktów „za Ducha”.',
    conditions: 'Moduł Pióra i Płomienie włączony.',
    exceptions: 'Katalog Duchów: DO WERYFIKACJI.',
    returnsMeeple: 'N/A',
    sourceStatus: 'component-dependent',
    verified: true,
    order: 220,
    keywords: ['pióra', 'duch'],
  },
];

export function filterSpiritIslandScoring(
  activeModuleIds: SpiritIslandScoringModuleId[],
  options: { showAll?: boolean; timingFilter?: SpiritIslandScoringTiming | 'all' } = {}
): SpiritIslandScoringRecord[] {
  const timing = options.timingFilter ?? 'all';
  const rows = options.showAll
    ? spiritIslandScoringRecords.slice()
    : (() => {
        const enabled = new Set(activeModuleIds);
        enabled.add('base');
        return spiritIslandScoringRecords.filter((r) => enabled.has(r.moduleId));
      })();
  return rows
    .filter((r) => {
      if (timing === 'all') return true;
      if (r.timing === 'both') return true;
      return r.timing === timing;
    })
    .sort((a, b) => a.order - b.order);
}

export function validateSpiritIslandScoringTables(): string[] {
  const issues: string[] = [];
  const ids = new Set<string>();
  for (const r of spiritIslandScoringRecords) {
    if (ids.has(r.id)) issues.push(`Duplicate SI scoring id ${r.id}`);
    ids.add(r.id);
    if (!r.element.trim() || !r.scoring.trim()) {
      issues.push(`Incomplete SI scoring ${r.id}`);
    }
  }
  const baseOnly = filterSpiritIslandScoring(['base']);
  if (!baseOnly.every((r) => r.moduleId === 'base')) {
    issues.push('Base filter leaked non-base SI rows');
  }
  if (!baseOnly.some((r) => r.id === 'si-terror-i')) {
    issues.push('Missing Terror I row');
  }
  if (!baseOnly.some((r) => r.id === 'si-fear-buildings')) {
    issues.push('Missing Fear-from-buildings row');
  }
  if (baseOnly.some((r) => r.moduleId === 'branch-and-claw')) {
    issues.push('Branch & Claw visible without module');
  }
  const withBc = filterSpiritIslandScoring(['base', 'branch-and-claw']);
  if (!withBc.some((r) => r.id === 'si-bc-adversary-scenario')) {
    issues.push('Branch & Claw scoring missing when enabled');
  }
  if (withBc.some((r) => r.moduleId === 'jagged-earth')) {
    issues.push('Jagged Earth visible without module');
  }
  const showAll = filterSpiritIslandScoring(['base'], { showAll: true });
  if (showAll.length <= baseOnly.length) {
    issues.push('showAll should include expansion SI rows');
  }
  const mid = filterSpiritIslandScoring(['base'], { timingFilter: 'mid-game' });
  if (!mid.some((r) => r.id === 'si-fear-buildings')) {
    issues.push('mid-game filter missing fear buildings');
  }
  if (mid.some((r) => r.id === 'si-terror-i' && r.timing !== 'both')) {
    issues.push('Terror I should remain via both timing');
  }
  const blocked = spiritIslandScoringRecords.filter((r) => r.sourceStatus === 'blocked');
  if (!blocked.some((r) => r.id === 'si-same-effect-edge')) {
    issues.push('Expected blocked same-effect edge row');
  }
  return issues;
}
